/**
 * Coupon Hooks
 */
import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { couponService } from '../services/couponService';
import { queryTags } from '../queryTags';
import { getQueryClient } from '../queryClient';
import type {
  CreateCouponPayload,
  UpdateCouponPayload,
  GetCouponsParams,
} from '../types/coupon.types';
import type { UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';

/**
 * Get coupons list hook
 */
export function useGetCoupons(
  params?: GetCouponsParams,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, 'queryKey' | 'queryFn'>
) {
  return useApiQuery({
    queryKey: [
      queryTags.coupon,
      params?.page || 1,
      params?.limit || 10,
      params?.search || '',
      params?.status,
      params?.valueType,
      params?.option,
    ],
    queryFn: () => couponService.getCoupons(params),
    ...options,
  });
}

/**
 * Get single coupon by ID hook
 */
export function useGetCoupon(
  id: string,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, 'queryKey' | 'queryFn'>
) {
  return useApiQuery({
    queryKey: [queryTags.coupon, id],
    queryFn: () => couponService.getCoupon(id),
    enabled: !!id,
    ...options,
  });
}

/**
 * Create coupon hook
 */
export function useCreateCoupon(
  options?: UseMutationOptions<any, any, CreateCouponPayload, any>
) {
  return useApiMutation<any, any, CreateCouponPayload, any>({
    mutationFn: ((data: CreateCouponPayload) => couponService.createCoupon(data)) as any,
    onSuccess: () => {
      getQueryClient().invalidateQueries({ queryKey: [queryTags.coupon] });
    },
    ...options,
  });
}

/**
 * Update coupon hook
 */
export function useUpdateCoupon(
  options?: UseMutationOptions<any, any, { id: string; data: UpdateCouponPayload }, any>
) {
  return useApiMutation<any, any, { id: string; data: UpdateCouponPayload }, any>({
    mutationFn: ((variables: { id: string; data: UpdateCouponPayload }) =>
      couponService.updateCoupon(variables.id, variables.data)) as any,
    onSuccess: async (data, variables, context, mutation) => {
      // Always invalidate and refetch queries to refresh the list
      await getQueryClient().invalidateQueries({ queryKey: [queryTags.coupon] });
      await getQueryClient().refetchQueries({ queryKey: [queryTags.coupon] });
      // Call custom onSuccess if provided
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context, mutation);
      }
    },
    onError: options?.onError,
  });
}

/**
 * Delete coupon hook
 */
export function useDeleteCoupon(
  options?: UseMutationOptions<any, any, string, any>
) {
  return useApiMutation<any, any, string, any>({
    mutationFn: ((id: string) => couponService.deleteCoupon(id)) as any,
    onSuccess: () => {
      getQueryClient().invalidateQueries({ queryKey: [queryTags.coupon] });
    },
    ...options,
  });
}

